import { useSelector } from 'react-redux';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import {
  DEFAULT_LIMIT_PAGE_SIZE,
  DEFAULT_SUPPORTED_FIELDS
} from './FilterMenus.service';
import { FILTER_TYPES } from 'common/constants/filters';
import { useMemo, useState } from 'react';
import useOrgTeams from './useOrgTeams';

const INITIAL_OFFSET = DEFAULT_LIMIT_PAGE_SIZE * 2;

const useFetchEntities = ({
  currentMenuItem = {},
  supportAllFields = false,
  customFetchProps = {}
}) => {
  const pmSensor = useSelector(pmIntegrationSelector) || {};
  const pmProvider = pmSensor?.sensor_id;
  const supportedCustomFieldsByProvider = DEFAULT_SUPPORTED_FIELDS[pmProvider];

  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [moreEntities, setMoreEntities] = useState([]);
  const [offset, setOffset] = useState(INITIAL_OFFSET);
  const orgTeams = useOrgTeams();

  const shouldShowMoreItem = useMemo(() => {
    return moreEntities.length > 0;
  }, [moreEntities]);

  const onInitPagination = () => {
    setOffset(INITIAL_OFFSET);
  };

  const filterByKeyword = (entities, keyword) => {
    if (currentMenuItem?.ignoreLocalSearch) return entities;

    return entities.filter(({ title }) =>
      title.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  const fetchData = async ({
    keyword = '',
    limit = DEFAULT_LIMIT_PAGE_SIZE,
    offset = 0
  } = {}) => {
    try {
      if (!currentMenuItem) return [];

      const shouldNotMakeApiCall =
        !currentMenuItem || !currentMenuItem?.getItems;

      if (currentMenuItem?.id === FILTER_TYPES.ORGANIZATION_TEAMS) {
        return filterByKeyword(orgTeams, keyword);
      }

      if (shouldNotMakeApiCall) return [];

      const isOnSearch = keyword.length > 0;
      const getItemsFn = isOnSearch
        ? currentMenuItem.onSearch
        : currentMenuItem.getItems;

      offset === 0 && setIsLoading(true);
      const data = await getItemsFn({
        entityType: currentMenuItem.id,
        supportedTypes: supportedCustomFieldsByProvider,
        supportAllFields,
        limit,
        pmSensorAuthId: pmSensor?.id,
        sensorId: pmSensor.sensor_id,
        offset,
        keyword,
        ...customFetchProps
      });

      const filteredData = filterByKeyword(data, keyword);
      return filteredData;
    } catch (error) {
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEntities = async ({ keyword = '' } = {}) => {
    setMoreEntities([]);
    const entities = await fetchData({ keyword });
    setEntities(entities);
    await fetchMoreEntities({ keyword });
    return entities;
  };

  const fetchMoreEntities = async ({
    keyword = '',
    offset = DEFAULT_LIMIT_PAGE_SIZE
  } = {}) => {
    const moreEntities = await fetchData({
      keyword,
      offset
    });
    setMoreEntities(moreEntities);
  };

  const showMoreEntities = ({ keyword }) => {
    setEntities(prevData => [...prevData, ...moreEntities]);
    fetchMoreEntities({ offset, keyword });
    setOffset(prevOffset => prevOffset + DEFAULT_LIMIT_PAGE_SIZE);
  };

  return {
    fetchEntities,
    showMoreEntities,
    isLoading,
    entities,
    shouldShowMoreItem,
    onInitPagination,
    keyword,
    setKeyword
  };
};

export default useFetchEntities;
